/**
 * @file eventService.js
 * @description Fire-and-forget analytics event tracking service. Validates event
 *   names, sanitizes metadata, and inserts rows into the events table without
 *   blocking any UI interaction.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/eventService
 */
import { supabase } from "./supabase";
import { getEventContext } from "./attribution";
import { sendGa4 } from "./ga4";

export const MAX_EVENT_NAME_LENGTH = 64;
export const MAX_EVENT_METADATA_BYTES = 4096;

function serializedSize(value) {
  return JSON.stringify(value || {}).length;
}

function toSafeMetadata(metadata = {}) {
  try {
    const source = metadata && typeof metadata === "object" ? metadata : {};
    const serialized = JSON.stringify(source);
    if (!serialized) return {};
    if (serializedSize(source) <= MAX_EVENT_METADATA_BYTES) {
      return JSON.parse(serialized);
    }

    const trimmed = {};
    for (const [key, value] of Object.entries(source)) {
      if (typeof value === "string") {
        if (value.length <= 256) trimmed[key] = value;
      } else if (typeof value === "number" || typeof value === "boolean" || value == null) {
        trimmed[key] = value;
      }
    }

    if (serializedSize(trimmed) <= MAX_EVENT_METADATA_BYTES) {
      return trimmed;
    }
  } catch (_) {}
  return {};
}

/**
 * @description Returns true if the event name is a non-empty string within the max length.
 * @param {string} event - Event name to validate
 * @returns {boolean}
 */
export function isValidEventName(event) {
  return typeof event === "string" && event.length > 0 && event.length <= MAX_EVENT_NAME_LENGTH;
}

/**
 * @description Tracks a user event. Fire-and-forget — never blocks the UI.
 *   Silently ignores invalid userId or event name.
 * @param {string} userId - The user's Supabase user ID
 * @param {string} event - Event name (must be ≤ MAX_EVENT_NAME_LENGTH chars)
 * @param {Object} [metadata] - Additional event metadata (auto-trimmed to fit MAX_EVENT_METADATA_BYTES)
 * @returns {void}
 */
export function trackEvent(userId, event, metadata = {}) {
  if (!userId || !isValidEventName(event)) return;
  // Fire-and-forget, but observable: .insert() resolves with { error } rather
  // than rejecting, so check it in .then() (the .catch() only catches network
  // throws). Failures never block the UI but are no longer silent.
  supabase
    .from("events")
    .insert({ user_id: userId, event, metadata: toSafeMetadata(metadata) })
    .then(({ error }) => { if (error) console.warn("[trackEvent] insert failed:", error.message); })
    .catch((e) => console.warn("[trackEvent] insert threw:", e?.message || e));
  // Mirror the meaningful business/lifecycle events to GA4 so the marketing
  // console sees the full funnel (acquisition → activation → revenue → retention),
  // not just top-of-funnel pageviews. Supabase above stays the source of truth;
  // GA4 gets only the whitelisted, non-PII, low-cardinality params below. The
  // revenue `purchase` event is fired separately at Stripe checkout completion
  // (Billing.jsx) where the real charge amount is known.
  mirrorLifecycleToGa4(event, metadata);
}

/**
 * Whitelist of in-house lifecycle events that should ALSO reach GA4, mapped to
 * their GA4 event name plus which metadata keys are safe to forward. Keys not
 * listed in `keep` (e.g. case_id / application_id UUIDs) are intentionally
 * dropped — high-cardinality ids are useless in GA4 and burn its param limits.
 * `add` supplies static params GA4 conventions expect (e.g. method on sign_up).
 */
const GA4_LIFECYCLE_MIRROR = {
  sign_up:        { name: "sign_up",        keep: ["role"],                       add: { method: "email" } },
  login:          { name: "login",          keep: [],                             add: { method: "email" } },
  case_post:      { name: "case_post",      keep: ["type"] },
  case_apply:     { name: "case_apply",     keep: ["type"] },
  hire_confirmed: { name: "hire_confirmed", keep: [] },
  boost_applied:  { name: "boost_applied",  keep: ["product_key", "free", "tier"] },
  plan_changed:   { name: "plan_changed",   keep: ["from", "to"] },
};

/**
 * Forward a whitelisted lifecycle event to GA4 with only safe params. No-op for
 * events not in the whitelist and when GA4 isn't loaded. Never throws.
 * @param {string} event - the in-house event name (EVENTS.*)
 * @param {Object} metadata - the same metadata passed to trackEvent
 */
function mirrorLifecycleToGa4(event, metadata = {}) {
  const spec = GA4_LIFECYCLE_MIRROR[event];
  if (!spec) return;
  const params = { ...(spec.add || {}) };
  for (const key of spec.keep) {
    const v = metadata?.[key];
    // Forward only present primitives — skip null/undefined/objects so GA4
    // never receives junk or high-cardinality blobs.
    if (v != null && (typeof v === "string" || typeof v === "number" || typeof v === "boolean")) {
      params[key] = v;
    }
  }
  sendGa4(spec.name, params);
}

/**
 * @description Records a named conversion/funnel event with attribution context.
 *   Works for BOTH guests (no session) and signed-in users:
 *     • signed-in  → user_id set (covered by the events_insert_own RLS policy)
 *     • guest      → user_id NULL + anon_id set (covered by events_insert_anon)
 *   Each row carries first-touch UTM, the current source_page, and referrer so
 *   the funnel can be sliced by campaign. Fire-and-forget; never blocks the UI.
 *   This is the in-house sink used by lib/analytics.js track().
 * @param {string} event - Event name (≤ MAX_EVENT_NAME_LENGTH chars)
 * @param {Object} [opts]
 * @param {string|null} [opts.userId] - Supabase user id, or null/undefined for a guest
 * @param {Object} [opts.metadata] - Extra metadata (auto-trimmed)
 * @returns {void}
 */
export function trackNamedEvent(event, { userId = null, metadata = {} } = {}) {
  if (!isValidEventName(event)) return;
  let ctx = {};
  try { ctx = getEventContext() || {}; } catch { ctx = {}; }

  // RLS: guest rows MUST have a null user_id and a non-null anon_id. Bail if a
  // guest somehow has no anon_id (storage blocked) — nothing to attribute to.
  const isGuest = !userId;
  if (isGuest && !ctx.anon_id) return;

  const row = {
    user_id:      userId || null,
    anon_id:      isGuest ? ctx.anon_id : null,
    event,
    metadata:     toSafeMetadata(metadata),
    utm_source:   ctx.utm_source   || null,
    utm_medium:   ctx.utm_medium   || null,
    utm_campaign: ctx.utm_campaign || null,
    source_page:  ctx.source_page  || null,
    referrer:     ctx.referrer     || null,
  };

  supabase
    .from("events")
    .insert(row)
    .then(({ error }) => { if (error) console.warn("[trackNamedEvent] insert failed:", error.message); })
    .catch((e) => console.warn("[trackNamedEvent] insert threw:", e?.message || e));
}

// Common event names
//
// Two tiers:
//   • LIFECYCLE / business events (login, logout, case_post, case_closed,
//     case_apply, hire_confirmed, boost_applied …) — surfaced in the Admin
//     Activity audit view. These answer "what meaningful thing happened?"
//   • Behavioral / analytics events (case_view, search_filter, case_save,
//     case_pass, boost_shown/blocked) — kept for funnel/ROI analysis but
//     HIDDEN from the Admin view (they're a clickstream, not an audit trail).
// The split is enforced in AdminPanel via LIFECYCLE_EVENTS.
export const EVENTS = {
  CASE_VIEW:        "case_view",
  CASE_APPLY:       "case_apply",
  CASE_SAVE:        "case_save",
  CASE_PASS:        "case_pass",
  CASE_POST:        "case_post",
  CASE_CONFIRM:     "case_confirm",
  CASE_CLOSED:      "case_closed",
  HIRE_CONFIRMED:   "hire_confirmed",
  // Application pipeline stages between "applied" and "hired". These had no
  // trace anywhere in the events table before this — only in case_journal,
  // which is scoped into a single case's own timeline and never surfaced in
  // Admin — so the Admin Activity log was structurally blind to the offer
  // pipeline, not just filtering it out. APP_* are agency-initiated (fired
  // from updateApplicationStatus); OFFER_* are therapist-initiated (fired
  // from respondToOffer, only after a confirmed rows-affected write).
  APP_REVIEWING:    "app_reviewing",
  APP_INTERVIEW:    "app_interview",
  APP_OFFERED:      "app_offered",
  APP_DECLINED:     "app_declined",
  OFFER_ACCEPTED:   "offer_accepted",
  OFFER_DECLINED:   "offer_declined",
  MESSAGE_SEND:     "message_send",
  PROFILE_UPDATE:   "profile_update",
  SEARCH_FILTER:    "search_filter",
  SIGN_UP:          "sign_up",
  LOGIN:            "login",
  LOGOUT:           "logout",
  PLAN_CHANGED:     "plan_changed",
  // Boost / monetization funnel — fired from the Case Performance tab so we
  // can measure tier-level conversion rates and which pitch copy converts.
  // boost_converted is server-side ONLY (process-boost-conversions cron) —
  // detects post-boost application lift; never fire from the client.
  BOOST_SHOWN:      "boost_shown",     // Recommended Next card rendered
  BOOST_CLICKED:    "boost_clicked",   // Apply button pressed (before spend resolves)
  BOOST_APPLIED:    "boost_applied",   // Spend succeeded (boost is now active)
  BOOST_CONVERTED:  "boost_converted", // Server-only: cron-detected post-boost application
  // Boost was the recommended action but the agency couldn't take it. Reason
  // is one of: 'spotlight_area_full' | 'insufficient_credits' | 'pro_required'.
  // Fires once per (case, product, reason) on render — not on click — so the
  // metric reflects "we surfaced an action the agency couldn't act on."
  // Used to size demand for fallback features (e.g. spotlight area waitlist)
  // before building them.
  BOOST_BLOCKED:    "boost_blocked",
  // Agency dashboard snapshot ended with a failed critical lane (cases,
  // applicants, plan or auth) or an implausible empty result, after retries.
  // Metadata: lane ("cases+applicants"), attempts, error (truncated, no ids).
  // Diagnostic only; deliberately NOT a lifecycle event in the Admin audit view.
  DASHBOARD_LOAD_FAILED: "dashboard_load_failed",
  // Agency notes usage (2026-10-18): zero notes from real agencies so far, and nothing recorded whether anyone
  // even opened the Notebook. Metadata: case_id, application_id (applicant notes), surface. Never the note text.
  // Behavioral, not lifecycle: kept out of the Admin audit view.
  NOTEBOOK_OPENED:      "notebook_opened",
  JOURNAL_NOTE_SAVED:   "journal_note_saved",
  APPLICANT_NOTE_SAVED: "applicant_note_saved",
};

/**
 * @description Aggregate boost ROI for an agency over a time window. Reads
 *   the events table directly (boost_applied + boost_converted), aggregates
 *   client-side, and returns a single shape the Case Performance tab can
 *   render without further joins.
 *
 *   The query intentionally does TWO bucketed pulls (current window + prior
 *   window) so the UI can render a "X vs Y last period" delta without a
 *   second round-trip.
 *
 * @param {string} agencyId
 * @param {number} [windowDays=30] - Days to look back for the "current" window
 * @returns {Promise<{
 *   current: { boostsApplied, creditsSpent, appsAttributed, byProduct, perCase },
 *   prior:   { boostsApplied, creditsSpent, appsAttributed }
 * }>}
 *
 *   `byProduct` is `{ [productKey]: { applied, credits, apps, ratio } }`
 *   `perCase` is `{ [caseId]: { topProduct, totalApps, totalCredits } }` —
 *   used by the per-card ROI badge on Case Performance.
 */
export async function loadBoostRoi(agencyId, windowDays = 30) {
  if (!agencyId) return emptyRoi();

  const now = new Date();
  const currentStart = new Date(now.getTime() - windowDays * 86400000).toISOString();
  const priorStart   = new Date(now.getTime() - windowDays * 2 * 86400000).toISOString();
  const priorEnd     = currentStart;

  // Spend/credits come from case_promotions (the SOURCE OF TRUTH the Market
  // Reach map already reads) — NOT from boost_applied telemetry events, which
  // can be absent (seed/migrated boosts) and made the headline "Spent 30d"
  // read $0 while the map showed real spend. Apps stay from boost_converted
  // events (post-boost application attribution from the conversions cron).
  const [{ data: convCurrent, error: errA }, { data: convPrior, error: errB }, { data: promos, error: errC }] = await Promise.all([
    supabase
      .from("events").select("metadata, created_at")
      .eq("user_id", agencyId).eq("event", "boost_converted")
      .gte("created_at", currentStart).limit(2000),
    supabase
      .from("events").select("metadata, created_at")
      .eq("user_id", agencyId).eq("event", "boost_converted")
      .gte("created_at", priorStart).lt("created_at", priorEnd).limit(2000),
    supabase
      .from("case_promotions").select("type, credits_spent, created_at, case_id")
      .eq("agency_id", agencyId).gte("created_at", priorStart).limit(2000),
  ]);

  if (errA || errB || errC) return emptyRoi();

  const allPromos   = promos || [];
  const curPromos   = allPromos.filter(p => p.created_at >= currentStart);
  const priorPromos = allPromos.filter(p => p.created_at >= priorStart && p.created_at < priorEnd);

  return {
    current: aggregateRoi(convCurrent || [], curPromos),
    prior:   aggregateRoi(convPrior || [], priorPromos, { aggOnly: true }),
  };
}

function emptyRoi() {
  return {
    current: { boostsApplied: 0, creditsSpent: 0, appsAttributed: 0, byProduct: {}, perCase: {} },
    prior:   { boostsApplied: 0, creditsSpent: 0, appsAttributed: 0 },
  };
}

function aggregateRoi(convRows, promoRows = [], { aggOnly = false } = {}) {
  let boostsApplied  = 0;
  let creditsSpent   = 0;
  let appsAttributed = 0;
  const byProduct = {};
  const perCase   = {};

  // Spend side — real credits actually spent, from case_promotions.
  for (const p of promoRows) {
    const credits = Number(p.credits_spent) || 0;
    boostsApplied += 1;
    creditsSpent  += credits;
    const key = p.type || "unknown";
    if (!byProduct[key]) byProduct[key] = { applied: 0, credits: 0, apps: 0, ratio: 0 };
    byProduct[key].applied += 1;
    byProduct[key].credits += credits;
  }

  // Apps side — post-boost application attribution, from boost_converted events.
  for (const r of convRows) {
    const meta = r.metadata || {};
    {
      const apps = Number(meta.applications) || 0;
      appsAttributed += apps;
      const key = meta.product_key || "unknown";
      if (!byProduct[key]) byProduct[key] = { applied: 0, credits: 0, apps: 0, ratio: 0 };
      byProduct[key].apps += apps;
      // Per-case rollup (used for on-card ROI badge)
      const caseId = meta.case_id;
      if (caseId && !aggOnly) {
        if (!perCase[caseId]) perCase[caseId] = { topProduct: key, totalApps: 0, totalCredits: 0 };
        perCase[caseId].totalApps    += apps;
        perCase[caseId].totalCredits += Number(meta.credits_spent) || 0;
        // Track the product that drove the most apps on this case so the
        // badge can name it (e.g. "+3 apps from Spotlight").
        if ((perCase[caseId].topProductApps || 0) < apps) {
          perCase[caseId].topProductApps = apps;
          perCase[caseId].topProduct = key;
        }
      }
    }
  }

  // Compute ratio per product (apps per credit) — protects against div-by-0
  for (const k of Object.keys(byProduct)) {
    const p = byProduct[k];
    p.ratio = p.credits > 0 ? p.apps / p.credits : 0;
  }

  if (aggOnly) {
    return { boostsApplied, creditsSpent, appsAttributed };
  }
  return { boostsApplied, creditsSpent, appsAttributed, byProduct, perCase };
}
